/**
 * useRowKeyboardNav
 * -----------------
 * Adds ↑ / ↓ / Home / End / Enter keyboard navigation to any list of rows.
 *
 * Usage:
 *   const { focusedIndex, getRowProps } = useRowKeyboardNav({
 *       items: visibleItems,
 *       onOpen: row => openEdit(row),
 *       disabled: !!editModalOpen,    // pause when a modal is open
 *   });
 *   ...
 *   {items.map((item, idx) => (
 *      <div {...getRowProps(idx)} key={item.id}>...</div>
 *   ))}
 *
 * The hook attaches a single window-level keydown listener (cleaned up on
 * unmount). Rows are auto-scrolled into view as focus moves.
 */
import { useEffect, useRef, useState, useCallback } from "react";

export default function useRowKeyboardNav({ items, onOpen, disabled = false }) {
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const refs = useRef([]);

    // Ensure refs array tracks current list length
    refs.current = items.map((_, i) => refs.current[i] || null);

    const scrollIntoView = useCallback((idx) => {
        const node = refs.current[idx];
        if (node?.scrollIntoView) {
            node.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    }, []);

    useEffect(() => {
        if (disabled || items.length === 0) return;
        const onKey = (e) => {
            // Ignore when the user is typing in an input/textarea/select
            const tag = (e.target?.tagName || "").toLowerCase();
            if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            if (e.key === "ArrowDown") {
                e.preventDefault();
                setFocusedIndex((i) => {
                    const n = Math.min(items.length - 1, (i < 0 ? 0 : i + 1));
                    scrollIntoView(n);
                    return n;
                });
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setFocusedIndex((i) => {
                    const n = Math.max(0, (i < 0 ? 0 : i - 1));
                    scrollIntoView(n);
                    return n;
                });
            } else if (e.key === "Home") {
                e.preventDefault();
                setFocusedIndex(0); scrollIntoView(0);
            } else if (e.key === "End") {
                e.preventDefault();
                const last = items.length - 1;
                setFocusedIndex(last); scrollIntoView(last);
            } else if (e.key === "Enter") {
                setFocusedIndex((i) => {
                    if (i >= 0 && i < items.length) onOpen?.(items[i]);
                    return i;
                });
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [items, disabled, scrollIntoView, onOpen]);

    const getRowProps = useCallback((idx) => ({
        ref: (el) => { refs.current[idx] = el; },
        "data-keynav-focused": focusedIndex === idx ? "true" : "false",
        tabIndex: -1,
    }), [focusedIndex]);

    return { focusedIndex, getRowProps };
}
